// Lightweight Markdown renderer for chat output. Covers the subset LLMs
// actually emit: headings, bullet/ordered lists, blockquotes, fenced code,
// horizontal rules, plus inline **bold**, *italic*, `code`, ~~strike~~,
// [text](url), and newlines.
//
// Why not react-markdown or marked? We don't need tables / raw HTML
// passthrough / image embedding — and dropping those removes the XSS
// surface we'd otherwise have to sanitize. This uses plain DOM nodes via
// createElement / createTextNode; no branch ever assigns to innerHTML
// (except the final commit of our own fragment).
//
// History: BUG-006 originally surfaced raw `**bold**` in Channels; the
// first fix handled only inline tokens. Users then saw raw `###` headings
// and bullet dashes, so this rev adds block-level handling.
import React, { useMemo } from "react";

const INLINE_RX = /(\*\*(.+?)\*\*|\*(.+?)\*|~~(.+?)~~|`(.+?)`|\[(.+?)\]\((https?:\/\/[^\s)]+)\))/g;

function tokenizeInline(line, target) {
  INLINE_RX.lastIndex = 0;
  let lastIdx = 0;
  let m;
  while ((m = INLINE_RX.exec(line)) !== null) {
    if (m.index > lastIdx) {
      target.appendChild(document.createTextNode(line.slice(lastIdx, m.index)));
    }
    if (m[2] !== undefined) {
      const el = document.createElement("strong");
      el.textContent = m[2];
      target.appendChild(el);
    } else if (m[3] !== undefined) {
      const el = document.createElement("em");
      el.textContent = m[3];
      target.appendChild(el);
    } else if (m[4] !== undefined) {
      const el = document.createElement("del");
      el.textContent = m[4];
      target.appendChild(el);
    } else if (m[5] !== undefined) {
      const el = document.createElement("code");
      el.textContent = m[5];
      el.style.cssText = "background:rgba(0,0,0,0.08);padding:1px 4px;border-radius:3px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.92em";
      target.appendChild(el);
    } else if (m[6] !== undefined && m[7] !== undefined) {
      const el = document.createElement("a");
      el.textContent = m[6];
      el.href = m[7];
      el.target = "_blank";
      el.rel = "noopener noreferrer";
      el.style.cssText = "color:inherit;text-decoration:underline";
      target.appendChild(el);
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < line.length) {
    target.appendChild(document.createTextNode(line.slice(lastIdx)));
  }
}

// Exported for unit testing the parser directly (matches the repo pattern of
// extracting logic into testable units, e.g. navRouting.js). Returns a
// DocumentFragment; callers/tests can inspect it via a container.
export function buildFragment(text) {
  const frag = document.createDocumentFragment();
  const src = String(text ?? "");
  // Empty/nullish input renders nothing. Without this the sole "" line hits the
  // blank-line rule below and emits a stray <br>, so an empty chat bubble (or a
  // MarkdownText before its first streamed token) showed a blank line of height.
  if (src === "") return frag;
  const lines = src.split("\n");
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block ``` ... ```
    if (/^\s*```/.test(line)) {
      const lang = line.replace(/^\s*```/, "").trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // consume closing fence (or EOF)
      const pre = document.createElement("pre");
      pre.style.cssText = "background:rgba(0,0,0,0.08);padding:8px 10px;border-radius:4px;overflow-x:auto;margin:6px 0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.88em;white-space:pre";
      const code = document.createElement("code");
      if (lang) code.className = `language-${lang}`;
      code.textContent = codeLines.join("\n");
      pre.appendChild(code);
      frag.appendChild(pre);
      continue;
    }

    // Horizontal rule: ---, ***, ___
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      const hr = document.createElement("hr");
      hr.style.cssText = "border:none;border-top:1px solid rgba(0,0,0,0.2);margin:8px 0";
      frag.appendChild(hr);
      i++;
      continue;
    }

    // Heading: # ... ###### (map H1..H6 → H3..H6 so in-chat headings stay subordinate)
    const headingMatch = line.match(/^(#{1,6})\s+(.*?)\s*#*\s*$/);
    if (headingMatch) {
      const level = Math.min(headingMatch[1].length + 2, 6);
      const el = document.createElement(`h${level}`);
      el.style.cssText = "font-weight:600;margin:6px 0 2px;font-size:1.04em;line-height:1.3";
      tokenizeInline(headingMatch[2], el);
      frag.appendChild(el);
      i++;
      continue;
    }

    // Blockquote: consecutive `> ...` lines fold into one <blockquote>
    if (/^\s*>\s?/.test(line)) {
      const bq = document.createElement("blockquote");
      bq.style.cssText = "border-left:3px solid rgba(0,0,0,0.25);padding:2px 8px;margin:4px 0;opacity:0.85";
      let first = true;
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        if (!first) bq.appendChild(document.createElement("br"));
        tokenizeInline(lines[i].replace(/^\s*>\s?/, ""), bq);
        first = false;
        i++;
      }
      frag.appendChild(bq);
      continue;
    }

    // Unordered list: -, *, + followed by space. Consecutive lines fold into <ul>.
    if (/^\s*[-*+]\s+/.test(line)) {
      const ul = document.createElement("ul");
      ul.style.cssText = "margin:4px 0;padding-left:22px";
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        const m = lines[i].match(/^\s*[-*+]\s+(.*)$/);
        const li = document.createElement("li");
        li.style.cssText = "margin:1px 0";
        tokenizeInline(m[1], li);
        ul.appendChild(li);
        i++;
      }
      frag.appendChild(ul);
      continue;
    }

    // Ordered list: `1.`, `2.`, ... followed by space. Consecutive lines fold into <ol>.
    if (/^\s*\d+\.\s+/.test(line)) {
      const ol = document.createElement("ol");
      ol.style.cssText = "margin:4px 0;padding-left:24px";
      // Start attribute respects the first number (handles "3. foo" / "4. bar" starting mid-count)
      const firstMatch = line.match(/^\s*(\d+)\.\s+/);
      if (firstMatch && firstMatch[1] !== "1") ol.setAttribute("start", firstMatch[1]);
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        const m = lines[i].match(/^\s*\d+\.\s+(.*)$/);
        const li = document.createElement("li");
        li.style.cssText = "margin:1px 0";
        tokenizeInline(m[1], li);
        ol.appendChild(li);
        i++;
      }
      frag.appendChild(ol);
      continue;
    }

    // Blank line → paragraph break (single <br>; consecutive plain lines also separate with <br>)
    if (line.trim() === "") {
      frag.appendChild(document.createElement("br"));
      i++;
      continue;
    }

    // Plain text line
    tokenizeInline(line, frag);
    // Preserve newline between consecutive plain lines, but don't emit a trailing <br>
    // right before a block element (headings/lists already own their top margin).
    const next = lines[i + 1];
    const nextStartsBlock = next !== undefined && (
      /^\s*```/.test(next) ||
      /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(next) ||
      /^#{1,6}\s+/.test(next) ||
      /^\s*>\s?/.test(next) ||
      /^\s*[-*+]\s+/.test(next) ||
      /^\s*\d+\.\s+/.test(next)
    );
    if (next !== undefined && !nextStartsBlock) {
      frag.appendChild(document.createElement("br"));
    }
    i++;
  }

  return frag;
}

export function MarkdownText({ text = "", style }) {
  // Throttle re-tokenization during rapid input (streaming tokens).
  // Without this the tokenizer + DOM rebuild fires per-token (~20-50
  // updates/sec), causing visible flicker as `*foo` flips between
  // raw asterisk and italic when the closing `*` arrives. The
  // throttled value updates at most every 120ms — matches human
  // reading cadence, eliminates jitter, and the FINAL value still
  // lands because the trailing-edge timer catches up after the
  // stream stops.
  const [throttledText, setThrottledText] = React.useState(text);
  const lastFlushRef = React.useRef(0);
  const trailingTimerRef = React.useRef(null);

  React.useEffect(() => {
    const FLUSH_MS = 120;
    const now = Date.now();
    const dueIn = Math.max(0, FLUSH_MS - (now - lastFlushRef.current));
    if (trailingTimerRef.current) clearTimeout(trailingTimerRef.current);
    if (dueIn === 0) {
      lastFlushRef.current = now;
      setThrottledText(text);
    } else {
      // Trailing-edge — schedule the next flush, ensuring the
      // FINAL text always lands even if the stream stops between
      // ticks (no token arrives to trigger another effect).
      trailingTimerRef.current = setTimeout(() => {
        lastFlushRef.current = Date.now();
        setThrottledText(text);
      }, dueIn);
    }
    return () => {
      if (trailingTimerRef.current) {
        clearTimeout(trailingTimerRef.current);
      }
    };
  }, [text]);

  const fragment = useMemo(() => buildFragment(throttledText), [throttledText]);

  const ref = React.useRef(null);
  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.innerHTML = "";
    node.appendChild(fragment);
  }, [fragment]);

  return <div ref={ref} style={style} />;
}
